import { Injectable, signal, OnDestroy } from '@angular/core';
import { Preferences } from '@capacitor/preferences';

export type ThemeMode = 'light' | 'dark' | 'system';

const THEME_KEY = 'dreampay-theme-mode';

@Injectable({ providedIn: 'root' })
export class ThemeService implements OnDestroy {
  readonly mode = signal<ThemeMode>('system');

  private mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  private mediaListener = (e: MediaQueryListEvent) => this.applySystemPreference(e.matches);

  async init(): Promise<void> {
    const { value } = await Preferences.get({ key: THEME_KEY });
    const saved = (value as ThemeMode) || 'system';
    this.mode.set(saved);
    this.applyMode(saved);
    this.mediaQuery.addEventListener('change', this.mediaListener);
  }

  async setMode(mode: ThemeMode): Promise<void> {
    this.mode.set(mode);
    await Preferences.set({ key: THEME_KEY, value: mode });
    this.applyMode(mode);
  }

  ngOnDestroy(): void {
    this.mediaQuery.removeEventListener('change', this.mediaListener);
  }

  private applyMode(mode: ThemeMode): void {
    const html = document.documentElement;

    switch (mode) {
      case 'dark':
        html.classList.add('dark');
        html.classList.remove('light');
        break;
      case 'light':
        html.classList.add('light');
        html.classList.remove('dark');
        break;
      case 'system':
        html.classList.remove('light');
        this.applySystemPreference(this.mediaQuery.matches);
        break;
    }
  }

  private applySystemPreference(isDark: boolean): void {
    if (this.mode() !== 'system') return;
    const html = document.documentElement;
    if (isDark) {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }
  }
}
